import React, { useMemo } from 'react'
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts'
import { Activity } from 'lucide-react'
import DashboardCard from '@/components/dashboard/DashboardCard'
import { cn } from '@/lib/utils'
import { paceBand, type Tone } from '@/lib/plain-language'

/** Worded pace-band tone → the design system's semantic text colour. */
const toneText: Record<Tone, string> = {
  positive: 'text-emerald-600',
  neutral: 'text-slate-600',
  negative: 'text-rose-600',
}

interface PaceDayData {
  occupancyPct: number
  baselineOccPct: number | null
  paceRatio: number | null
}

interface PaceChartProps {
  paceData: Record<string, PaceDayData>
}

function formatDate(iso: string): string {
  const d = new Date(iso + 'T00:00:00')
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric' })
}

const PaceTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null
  const actual = payload.find((p: any) => p.dataKey === 'actual')?.value
  const baseline = payload.find((p: any) => p.dataKey === 'baseline')?.value
  // Pace reads as a word, never a bare multiplier (revenue-vocabulary §1). The actual and
  // usual occupancy already sit above, so the band label is the plain conclusion.
  const band = actual != null && baseline != null && baseline > 0 ? paceBand(actual / baseline) : null
  return (
    <div className="rounded-lg border border-border/60 bg-white px-3 py-2 shadow-lg shadow-black/5">
      <p className="text-[11px] font-medium text-muted-foreground">{label}</p>
      <p className="text-sm font-semibold text-foreground">{actual?.toFixed(1)}% full</p>
      {baseline != null && (
        <p className="text-xs text-muted-foreground">usually ~{baseline.toFixed(1)}% by now</p>
      )}
      {band && <p className={cn('text-xs font-semibold mt-0.5', toneText[band.tone])}>{band.label}</p>}
    </div>
  )
}

export default function PaceChart({ paceData }: PaceChartProps) {
  const { chartData, avgPace, aheadCount, totalCount } = useMemo(() => {
    const entries = Object.entries(paceData).sort(([a], [b]) => a.localeCompare(b))
    const data = entries.map(([date, d]) => ({
      label: formatDate(date),
      actual: d.occupancyPct,
      baseline: d.baselineOccPct,
    }))

    let paceSum = 0
    let paceCount = 0
    let ahead = 0
    for (const d of entries) {
      const ratio = d[1].paceRatio
      if (ratio != null) {
        paceSum += ratio
        paceCount++
        if (ratio >= 1) ahead++
      }
    }

    return {
      chartData: data,
      avgPace: paceCount > 0 ? paceSum / paceCount : null,
      aheadCount: ahead,
      totalCount: paceCount,
    }
  }, [paceData])

  const avgBand = avgPace != null ? paceBand(avgPace) : null

  return (
    <DashboardCard
      title="Booking pace"
      description="Next 30 nights against how full this property usually is by now"
      icon={<Activity className="h-4 w-4 shrink-0 text-muted-foreground" />}
      help={{
        content:
          "How full each of the next 30 nights is compared with how full it usually is this far out. 'Ahead' means those nights are filling faster than your own history — a different question from the year-on-year Pace band above.",
        learnMore: '/guides/occupancy-metrics',
      }}
      action={
        avgBand ? (
          <div className="text-right">
            <span className={cn('text-base font-bold', toneText[avgBand.tone])}>{avgBand.label}</span>
            <p className="text-[11px] text-muted-foreground tabular-nums">
              {aheadCount} of {totalCount} nights ahead
            </p>
          </div>
        ) : undefined
      }
    >
      <>
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={chartData}>
            <defs>
              <linearGradient id="paceActualGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#0d9488" stopOpacity={0.15} />
                <stop offset="95%" stopColor="#0d9488" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 10, fill: '#64748b' }}
              tickLine={false}
              axisLine={{ stroke: '#e2e8f0' }}
              interval="preserveStartEnd"
            />
            <YAxis
              tick={{ fontSize: 10, fill: '#64748b' }}
              tickLine={false}
              axisLine={false}
              tickFormatter={(v: number) => `${v}%`}
              width={40}
            />
            <Tooltip content={<PaceTooltip />} />
            <Area
              type="monotone"
              dataKey="baseline"
              stroke="#94a3b8"
              strokeDasharray="4 3"
              strokeWidth={1.5}
              fill="none"
              dot={false}
              name="Baseline"
            />
            <Area
              type="monotone"
              dataKey="actual"
              stroke="#0d9488"
              strokeWidth={2}
              fill="url(#paceActualGrad)"
              dot={false}
              name="Actual"
            />
          </AreaChart>
        </ResponsiveContainer>
      </>
    </DashboardCard>
  )
}
